/**
 * BrainDumpNoteDetail — full-screen overlay for viewing/editing a note.
 *
 * A13-N11. Follows settingsView overlay pattern (BrainDumpPage, WalletPage, etc.)
 *
 * Props:
 *  - noteId: UUID string
 *  - onClose: () => void (back to BrainDumpPage)
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';
import { useConfirmDialog } from '../overlay/ConfirmDialog';
import EntityLinkCard from './EntityLinkCard';
import TagChip from './TagChip';
import AttachmentViewer from './AttachmentViewer';
import WebEnrichmentDialog from './WebEnrichmentDialog';

const API_PREFIX = '/api/v1/aif/brain-dump';

export default function BrainDumpNoteDetail({ noteId, onClose }) {
  const { t } = useTranslation();
  const { confirm, ConfirmDialogElement } = useConfirmDialog();

  const [note, setNote] = useState(null);
  const [links, setLinks] = useState([]);
  const [attachmentUrls, setAttachmentUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingSummary, setEditingSummary] = useState(false);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [enrichDialogOpen, setEnrichDialogOpen] = useState(false);

  const loadNote = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const noteRes = await apiClient.get(`${API_PREFIX}/notes/${noteId}`);
      const noteData = noteRes.data ?? noteRes;
      setNote(noteData);
      setSummaryDraft(noteData.structured_summary || '');

      const [linksRes, urlsRes] = await Promise.all([
        apiClient.get(`${API_PREFIX}/notes/${noteId}/links`).catch(() => ({})),
        apiClient.get(`${API_PREFIX}/notes/${noteId}/attachment-urls`).catch(() => ({})),
      ]);
      setLinks((linksRes.data ?? linksRes).links || []);
      setAttachmentUrls((urlsRes.data ?? urlsRes).urls || []);
    } catch (err) {
      setError(err.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (noteId) loadNote();
  }, [noteId, loadNote]);

  async function handleSaveSummary() {
    try {
      await apiClient.patch(`${API_PREFIX}/notes/${noteId}`, {
        structured_summary: summaryDraft,
      });
      setEditingSummary(false);
      await loadNote();
    } catch (err) {
      setError(err.message || t('brainDump.saveError', 'Speichern fehlgeschlagen'));
    }
  }

  async function handleConfirmLink(linkId) {
    try {
      await apiClient.patch(`${API_PREFIX}/links/${linkId}/confirm`, {});
      await loadNote();
    } catch (err) {
      setError(err.message || t('brainDump.confirmError', 'Bestätigung fehlgeschlagen'));
    }
  }

  async function handleRejectLink(linkId) {
    try {
      await apiClient.patch(`${API_PREFIX}/links/${linkId}/reject`, {});
      setLinks(links.filter((l) => l.id !== linkId));
    } catch (err) {
      setError(err.message || t('brainDump.rejectError', 'Ablehnung fehlgeschlagen'));
    }
  }

  async function handleAddTag() {
    const name = newTagName.trim();
    if (!name) return;
    try {
      await apiClient.post(`${API_PREFIX}/notes/${noteId}/tags`, { tag_name: name });
      setNewTagName('');
      await loadNote();
    } catch (err) {
      setError(err.message || t('brainDump.tagError', 'Tag konnte nicht hinzugefügt werden'));
    }
  }

  async function handleRemoveTag(tagId) {
    try {
      await apiClient.delete(`${API_PREFIX}/tags/${tagId}`);
      await loadNote();
    } catch (err) {
      setError(err.message || t('brainDump.tagRemoveError', 'Tag konnte nicht entfernt werden'));
    }
  }

  async function handleToggleVisibility() {
    const next = note.visibility === 'private' ? 'tenant_shared' : 'private';
    try {
      await apiClient.patch(`${API_PREFIX}/notes/${noteId}`, { visibility: next });
      await loadNote();
    } catch (err) {
      setError(err.message || t('brainDump.visibilityError', 'Sichtbarkeit konnte nicht geändert werden'));
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: t('brainDump.deleteConfirm', 'Notiz wirklich löschen?'),
      description: t('brainDump.deleteConfirmBody', 'Diese Aktion kann nicht rückgängig gemacht werden.'),
      destructive: true,
    });
    if (!ok) return;
    try {
      await apiClient.delete(`${API_PREFIX}/notes/${noteId}`);
      onClose();
    } catch (err) {
      setError(err.message || t('brainDump.deleteError', 'Löschen fehlgeschlagen'));
    }
  }

  // Loading state
  if (loading) {
    return (
      <div
        className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight"
        data-testid="note-detail-page"
      >
        <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
            data-testid="note-detail-back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
            {t('brainDump.noteDetail', 'Notiz-Detail')}
          </span>
        </header>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-pulse text-[var(--aif-text-muted)]">{t('common.loading', 'Lade...')}</div>
        </div>
      </div>
    );
  }

  // Error state (no note loaded)
  if (error && !note) {
    return (
      <div
        className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight"
        data-testid="note-detail-page"
      >
        <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
            data-testid="note-detail-back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
            </svg>
          </button>
        </header>
        <div className="flex-1 flex items-center justify-center p-6 text-[var(--aif-color-danger)]">{error}</div>
      </div>
    );
  }

  if (!note) return null;

  const captureDate = new Date(note.captured_at);
  const formattedDate = captureDate.toLocaleString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div
      className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col animate-slideInRight"
      data-testid="note-detail-page"
    >
      {/* Header */}
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button
          onClick={onClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]"
          data-testid="note-detail-back"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {t('brainDump.noteDetail', 'Notiz-Detail')}
        </span>
        <div className="ml-auto">
          <button
            onClick={handleToggleVisibility}
            className={`text-xs px-2 py-1 rounded border transition min-h-[var(--aif-touch-min)] ${
              note.visibility === 'private'
                ? 'bg-[var(--aif-color-warning-light)] border-[var(--aif-color-warning-border)] text-[var(--aif-color-warning)]'
                : 'bg-[var(--aif-color-success-light)] border-[var(--aif-color-success-border)] text-[var(--aif-color-success)]'
            }`}
            data-testid="visibility-toggle"
          >
            {note.visibility === 'private'
              ? t('brainDump.private', 'Privat')
              : t('brainDump.shared', 'Geteilt')}
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          {/* Meta info */}
          <div className="text-sm text-[var(--aif-text-muted)]">
            {note.location_label && <span data-testid="location-label">{note.location_label} · </span>}
            <span data-testid="capture-date">{formattedDate}</span>
          </div>

          {/* Document type badge */}
          {note.document_type && note.document_type !== 'note' && (
            <div>
              <span className="inline-block px-2 py-1 bg-[var(--aif-color-warning-light)] text-[var(--aif-color-warning)] text-xs rounded">
                {note.document_type}
              </span>
            </div>
          )}

          {/* Attachments */}
          <AttachmentViewer urls={attachmentUrls} />

          {/* Structured Summary (editable) */}
          <div>
            <div className="flex justify-between items-center mb-1">
              <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide">
                {t('brainDump.summary', 'Zusammenfassung (KI)')}
              </h2>
              {!editingSummary && (
                <button
                  onClick={() => setEditingSummary(true)}
                  className="text-xs text-[var(--aif-color-primary)] hover:underline"
                  data-testid="edit-summary-btn"
                >
                  {t('brainDump.edit', 'Bearbeiten')}
                </button>
              )}
            </div>
            {editingSummary ? (
              <div>
                <textarea
                  value={summaryDraft}
                  onChange={(e) => setSummaryDraft(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-[var(--aif-border-default)] rounded-lg text-sm bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--aif-color-primary)]"
                  data-testid="summary-textarea"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleSaveSummary}
                    className="px-3 py-1 bg-[var(--aif-color-primary)] text-white rounded text-sm hover:opacity-90 min-h-[var(--aif-touch-min)]"
                    data-testid="save-summary-btn"
                  >
                    {t('common.save', 'Speichern')}
                  </button>
                  <button
                    onClick={() => {
                      setSummaryDraft(note.structured_summary || '');
                      setEditingSummary(false);
                    }}
                    className="px-3 py-1 text-[var(--aif-text-secondary)] hover:bg-[var(--aif-bg-surface)] rounded text-sm min-h-[var(--aif-touch-min)]"
                  >
                    {t('common.cancel', 'Abbrechen')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-[var(--aif-text-primary)] leading-relaxed" data-testid="summary-text">
                {note.structured_summary || (
                  <span className="text-[var(--aif-text-muted)] italic">
                    {note.processing_status === 'completed'
                      ? t('brainDump.noSummary', 'Keine Zusammenfassung')
                      : t('brainDump.processing', 'Wird verarbeitet...')}
                  </span>
                )}
              </p>
            )}
          </div>

          {/* Raw text (if different from summary) */}
          {note.raw_text && note.raw_text !== note.structured_summary && (
            <div>
              <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-1">
                {t('brainDump.originalText', 'Originaltext')}
              </h2>
              <p className="text-sm text-[var(--aif-text-secondary)] whitespace-pre-wrap">{note.raw_text}</p>
            </div>
          )}

          {/* Linked entities */}
          {links.length > 0 && (
            <div>
              <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                {t('brainDump.linkedWith', 'Verlinkt mit')}
              </h2>
              <div className="space-y-2" data-testid="links-list">
                {links.map((l) => (
                  <EntityLinkCard
                    key={l.id}
                    link={l}
                    onConfirm={() => handleConfirmLink(l.id)}
                    onReject={() => handleRejectLink(l.id)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div>
            <h2 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
              Tags
            </h2>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="tags-list">
              {(note.tags || []).map((tag) => (
                <TagChip key={tag.id} tag={tag} onRemove={() => handleRemoveTag(tag.id)} />
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                placeholder={t('brainDump.addTag', 'Tag hinzufügen...')}
                className="flex-1 px-3 py-1 border border-[var(--aif-border-default)] rounded text-sm bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] placeholder:text-[var(--aif-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--aif-color-primary)]"
                data-testid="tag-input"
              />
              <button
                onClick={handleAddTag}
                className="px-3 py-1 bg-[var(--aif-bg-surface)] hover:bg-[var(--aif-border-default)] text-[var(--aif-text-secondary)] rounded text-sm border border-[var(--aif-border-default)] min-h-[var(--aif-touch-min)] min-w-[var(--aif-touch-min)]"
                data-testid="add-tag-btn"
              >
                +
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-[var(--aif-border-default)]">
            <button
              onClick={() => setEnrichDialogOpen(true)}
              className="px-3 py-2 text-sm text-[var(--aif-color-primary)] hover:bg-[var(--aif-bg-surface)] rounded transition min-h-[44px]"
              data-testid="enrich-btn"
            >
              {t('brainDump.enrichBtn', 'Web-Info finden')}
            </button>
            <button
              onClick={handleDelete}
              className="ml-auto px-3 py-2 text-sm text-[var(--aif-color-danger)] hover:bg-[var(--aif-color-danger-light)] rounded transition min-h-[44px]"
              data-testid="delete-btn"
            >
              {t('brainDump.delete', 'Löschen')}
            </button>
          </div>

          {/* Error toast */}
          {error && (
            <div className="text-sm text-[var(--aif-color-danger)] bg-[var(--aif-color-danger-light)] px-3 py-2 rounded">
              {error}
            </div>
          )}
        </div>
      </div>

      <WebEnrichmentDialog
        noteId={noteId}
        open={enrichDialogOpen}
        onClose={() => setEnrichDialogOpen(false)}
        onComplete={loadNote}
      />
      {ConfirmDialogElement}
    </div>
  );
}
